import { CONSENT_VERSION } from '@/config/consent';

type ConsentState = { 
  version: string; 
  necessary: true; 
  analytics: boolean; 
  marketing: boolean; 
  preferences: boolean; 
  ts: number 
};

const KEY = 'cw_consent';

export const getConsent = (): ConsentState | null => {
  try { 
    const v = JSON.parse(localStorage.getItem(KEY) || 'null'); 
    return v?.version === CONSENT_VERSION ? v : null; 
  } catch { 
    return null; 
  }
};

export const setConsent = (s: Omit<ConsentState, 'version' | 'ts'>) => {
  const v: ConsentState = { ...s, version: CONSENT_VERSION, ts: Date.now(), necessary: true };
  localStorage.setItem(KEY, JSON.stringify(v));
  applyConsentToPage(v);
  return v;
};

// Google Consent Mode v2
// A appeler très tôt avec "denied" par défaut
export const initConsentDefaults = () => {
  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(...args: any[]) { (window as any).dataLayer.push(args); }
  (window as any).gtag = gtag;
  gtag('consent', 'default', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
  });
  const saved = getConsent();
  if (saved) applyConsentToPage(saved);
};

export const applyConsentToPage = (c: ConsentState) => {
  const gtag = (window as any).gtag || ((...args: any[]) => {});
  gtag('consent', 'update', {
    ad_storage: c.marketing ? 'granted' : 'denied',
    analytics_storage: c.analytics ? 'granted' : 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    ad_user_data: c.marketing ? 'granted' : 'denied',
    ad_personalization: c.marketing ? 'granted' : 'denied',
  });
  enableDeferredScripts(c);
  gateIframes(c);
};

export const categoryAllowed = (c: ConsentState | null, cat: keyof ConsentState) => !!c && (c as any)[cat] === true;

// Active les <script type="text/plain" data-consent="analytics|marketing|preferences">
export const enableDeferredScripts = (c: ConsentState) => {
  document.querySelectorAll<HTMLScriptElement>('script[type="text/plain"][data-consent]').forEach(s => {
    const cat = s.dataset.consent as keyof ConsentState;
    if (categoryAllowed(c, cat)) {
      const ns = document.createElement('script');
      [...s.attributes].forEach(a => { if (a.name !== 'type') ns.setAttribute(a.name, a.value); });
      ns.removeAttribute('data-consent'); 
      ns.type = 'text/javascript';
      ns.text = s.text; 
      if (s.src) ns.src = s.src;
      s.replaceWith(ns);
    }
  });
};

// Iframes gate (YouTube, etc.)
export const gateIframes = (c: ConsentState) => {
  document.querySelectorAll<HTMLIFrameElement>('iframe[data-consent]').forEach(iframe => {
    const cat = iframe.dataset.consent as keyof ConsentState;
    if (categoryAllowed(c, cat)) {
      const src = iframe.dataset.src || '';
      if (src && !iframe.src) iframe.src = src;
      iframe.classList.remove('cw-iframe-blocked');
    } else {
      if (!iframe.dataset.src && iframe.src) { 
        iframe.dataset.src = iframe.src; 
        iframe.src = 'about:blank'; 
      }
      iframe.classList.add('cw-iframe-blocked');
    }
  });
};

// QA Function to audit consent compliance
export const runConsentAudit = () => {
  const report = {
    scripts: {
      deferred: [] as Array<{ src: string; category: string; compliant: boolean }>,
      active: [] as Array<{ src: string; category: string; blocked: boolean }>,
    },
    iframes: {
      gated: [] as Array<{ src: string; category: string; compliant: boolean }>,
    },
    consentMode: {
      initialized: !!(window as any).gtag,
      dataLayer: Array.isArray((window as any).dataLayer),
    },
    ui: {
      manageLink: !!document.getElementById('cw-manage-cookies'),
    }
  };

  // Check deferred scripts
  document.querySelectorAll<HTMLScriptElement>('script[type="text/plain"][data-consent]').forEach(script => {
    const category = script.dataset.consent || 'unknown';
    const src = script.src || script.innerHTML.substring(0, 100);
    report.scripts.deferred.push({
      src,
      category,
      compliant: true
    });
  });

  // Check active scripts that should be deferred
  document.querySelectorAll<HTMLScriptElement>('script[src]').forEach(script => {
    const src = script.src;
    let shouldBeDeferred = false;
    let category = 'unknown';

    // Check against known patterns
    if (/googletagmanager\.com|google-analytics\.com|gtag\/js/i.test(src)) {
      shouldBeDeferred = true;
      category = 'analytics';
    } else if (/facebook\.net|connect\.facebook\.net|tiktokcdn|tiktok\.com/i.test(src)) {
      shouldBeDeferred = true;
      category = 'marketing';
    } else if (/youtube\.com|youtu\.be|vimeo\.com/i.test(src)) {
      shouldBeDeferred = true;
      category = 'preferences';
    }

    if (shouldBeDeferred) {
      report.scripts.active.push({
        src,
        category,
        blocked: script.type === 'text/plain'
      });
    }
  });

  // Check gated iframes
  document.querySelectorAll<HTMLIFrameElement>('iframe[data-consent]').forEach(iframe => {
    const category = iframe.dataset.consent || 'unknown';
    const src = iframe.dataset.src || iframe.src || 'unknown';
    report.iframes.gated.push({
      src,
      category,
      compliant: true
    });
  });

  console.log('🔍 Consent Audit Report:', report);
  return report;
};